import React, { useEffect, useRef, useState } from "react";

/**
 * Signature pad — supports both drawing (mouse / touch) AND typed signature.
 * Returns base64 PNG data URL on save via onChange(dataUrl).
 */
export default function SignaturePad({ value, onChange, height = 140 }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState(value ? "drawn" : "draw"); // draw | type

  useEffect(() => {
    if (mode !== "draw") return;
    const c = canvasRef.current; if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.offsetWidth, h = height;
    c.width = w * ratio; c.height = h * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = "#0F1644"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
    if (value && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, h);
      img.src = value;
    }
  }, [mode, height]);

  const pos = (e) => {
    const c = canvasRef.current.getBoundingClientRect();
    if (e.touches) return { x: e.touches[0].clientX - c.left, y: e.touches[0].clientY - c.top };
    return { x: e.clientX - c.left, y: e.clientY - c.top };
  };
  const start = (e) => { e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); setDrawing(true); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (!drawing) return; setDrawing(false); const dataUrl = canvasRef.current.toDataURL("image/png"); onChange?.(dataUrl); };
  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    onChange?.("");
  };

  if (mode === "type") {
    return (
      <div className="space-y-2">
        <input
          value={value && value.startsWith("data:") ? "" : (value || "")}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Type your full legal name as your signature"
          className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 focus-visible:ring-2 focus-visible:ring-[#00C7FF] focus-visible:outline-none"
          style={{ fontFamily: "'Dancing Script', cursive, serif", fontSize: 30 }}
          data-testid="signature-typed-input"
        />
        <div className="flex justify-between text-xs">
          <button type="button" onClick={() => setMode("draw")} className="text-brand-primary underline" data-testid="signature-mode-draw">Draw instead</button>
          <button type="button" onClick={() => onChange?.("")} className="text-slate-500 underline" data-testid="signature-clear-typed">Clear</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-slate-300 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height, display: "block", touchAction: "none", cursor: "crosshair" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={() => setMode("type")} className="text-brand-primary underline" data-testid="signature-mode-type">Type instead</button>
        <button type="button" onClick={clear} className="text-slate-500 underline" data-testid="signature-clear">Clear</button>
      </div>
    </div>
  );
}
